import React from "react";
import { useNavigate } from "react-router-dom";
import { Crown, BookOpen, Cpu, Headphones, BarChart3, ShieldCheck, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import MobileMockup from "@/components/MobileMockup";

const STATS = [
  { label: "10 Years Question Bank", title: "20,000+ MCQs", icon: BookOpen, color: "from-blue-500 to-indigo-500" },
  { label: "Daily Grand Tests & Quizzes", title: "800+ Exams", icon: BarChart3, color: "from-indigo-500 to-indigo-500" },
  { label: "Conceptual & High Yield", title: "AI Explanations", icon: Cpu, color: "from-indigo-500 to-cyan-500" },
  { label: "24×7 Personalised Help", title: "AI Tutor", icon: Headphones, color: "from-cyan-500 to-blue-600" },
];

const PLAY_STORE = "https://play.google.com/store/apps/details?id=app.emergent.neetpgprep7c9f4774b";

export default function Hero() {
  const navigate = useNavigate();
  return (
    <section id="home" className="relative pt-28 pb-16 md:pt-32 md:pb-24 overflow-hidden">
      {/* Background green wash */}
      <div className="absolute inset-0 bg-gradient-to-br from-blue-50/70 via-white to-indigo-50/40" />
      <div className="absolute inset-0 grain opacity-60" />

      {/* Floating + icons */}
      {[
        { t: "10%", l: "8%", s: 22 },
        { t: "22%", l: "85%", s: 18 },
        { t: "58%", l: "5%", s: 16 },
        { t: "78%", l: "82%", s: 24 },
        { t: "40%", l: "48%", s: 14 },
      ].map((p, i) => (
        <Plus
          key={i}
          className="float-plus text-blue-300/60 absolute"
          style={{ top: p.t, left: p.l, width: p.s, height: p.s, animationDelay: `${i * 0.4}s` }}
        />
      ))}

      {/* ECG line */}
      <svg
        className="absolute left-0 right-0 top-1/2 -translate-y-1/2 w-full h-40 opacity-30 pointer-events-none"
        viewBox="0 0 1200 200"
        preserveAspectRatio="none"
        aria-hidden="true"
      >
        <path
          className="ecg-path"
          d="M0 100 L200 100 L240 100 L260 60 L280 140 L300 30 L320 170 L340 100 L600 100 L640 100 L660 70 L680 130 L700 50 L720 150 L740 100 L1200 100"
          fill="none"
          stroke="#1E5FD8"
          strokeWidth="2"
        />
      </svg>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-12 gap-10 items-center">
        {/* Left content */}
        <div className="lg:col-span-7">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-amber-50 border border-amber-200 mb-5">
            <Crown className="h-3.5 w-3.5 text-amber-600" />
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-amber-700">DearDr Pro</span>
          </div>

          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-slate-900 leading-[1.05]">
            Crack <span className="whitespace-nowrap">NEET PG</span><br />
            with <span className="text-blue-600">Dear Dr</span>
          </h1>

          <p className="mt-5 text-base sm:text-lg text-slate-600 max-w-xl leading-relaxed">
            India&apos;s most trusted AI-Powered platform for NEET PG &amp; INI-CET preparation. 20,000+ MCQs,
            800+ exams, AI explanations and analytics — all in one place.
          </p>

          {/* Stat cards */}
          <div className="mt-8 grid grid-cols-2 sm:grid-cols-4 gap-3">
            {STATS.map((s) => {
              const I = s.icon;
              return (
                <div
                  key={s.title}
                  data-testid={`hero-stat-${s.title.toLowerCase().split(" ")[0]}`}
                  className="bg-white border border-slate-200 rounded-2xl p-3 lift"
                >
                  <div className={`h-10 w-10 rounded-xl bg-gradient-to-br ${s.color} flex items-center justify-center mb-2 shadow-md shadow-blue-600/10`}>
                    <I className="h-5 w-5 text-white" strokeWidth={2.2} />
                  </div>
                  <div className="text-[13px] font-bold text-slate-900 leading-tight">{s.title}</div>
                  <div className="text-[11px] text-slate-500 mt-0.5">{s.label}</div>
                </div>
              );
            })}
          </div>

          {/* CTAs */}
          <div className="mt-8 flex flex-wrap items-center gap-3">
            <Button
              size="lg"
              onClick={() => navigate("/register")}
              data-testid="hero-cta-trial"
              className="bg-blue-600 hover:bg-blue-700 text-white rounded-full pl-3 pr-6 h-14 text-base font-semibold shadow-lg shadow-blue-600/25"
            >
              <span className="h-9 w-9 rounded-full bg-white/20 flex items-center justify-center mr-2">
                <Crown className="h-4 w-4 text-amber-300" />
              </span>
              <span className="flex flex-col items-start leading-tight">
                <span>7 Days Free Trial</span>
                <span className="text-[10px] font-normal opacity-80">No credit card required</span>
              </span>
            </Button>

            <a
              href={PLAY_STORE}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-cta-play-store"
              className="h-14 inline-flex items-center gap-2 bg-slate-900 hover:bg-slate-800 text-white rounded-2xl px-5 transition-colors"
            >
              <svg viewBox="0 0 24 24" className="h-6 w-6"><path fill="#34D399" d="M4 2.5v19l9.5-9.5z"/><path fill="#FBBF24" d="M13.5 12L4 21.5 17 14z"/><path fill="#F87171" d="M17 14l-3.5-2L4 2.5z"/><path fill="#60A5FA" d="M17 14l4-2.3c1.3-.75 1.3-2.65 0-3.4L17 10l-3.5 2z"/></svg>
              <span className="flex flex-col items-start leading-tight">
                <span className="text-[10px] opacity-80">GET IT ON</span>
                <span className="font-semibold text-sm">Google Play</span>
              </span>
            </a>

            <div
              data-testid="hero-cta-app-store"
              className="h-14 inline-flex items-center gap-2 bg-slate-900/90 text-white rounded-2xl px-5 opacity-80 cursor-not-allowed relative"
              title="Coming soon"
            >
              <svg viewBox="0 0 24 24" className="h-6 w-6" fill="white"><path d="M16.365 1.43c0 1.14-.45 2.16-1.17 2.95-.84.92-2.16 1.62-3.25 1.53-.13-1.15.4-2.32 1.13-3.07.81-.83 2.2-1.43 3.29-1.41zm3.43 17.71c-.61 1.4-.91 2.03-1.69 3.27-1.09 1.73-2.63 3.88-4.53 3.9-1.69.02-2.13-1.1-4.43-1.09-2.3.01-2.78 1.12-4.48 1.1-1.91-.02-3.36-1.95-4.45-3.68C-.59 18.5-1.04 14.07.96 11.4c1.18-1.58 3.05-2.58 4.8-2.58 1.79 0 2.91 1 4.39 1 1.44 0 2.32-1 4.39-1 1.57 0 3.23.85 4.42 2.32-3.88 2.13-3.25 7.69 1.83 9z"/></svg>
              <span className="flex flex-col items-start leading-tight">
                <span className="text-[10px] opacity-80">COMING SOON</span>
                <span className="font-semibold text-sm">App Store</span>
              </span>
            </div>
          </div>

          <div className="mt-6 flex items-center gap-2 text-sm text-slate-600">
            <ShieldCheck className="h-4 w-4 text-blue-600" />
            <span>Trusted by <b className="text-slate-900">50,000+</b> NEET PG aspirants</span>
          </div>
        </div>

        {/* Right - Phone */}
        <div className="lg:col-span-5 flex justify-center lg:justify-end">
          <MobileMockup />
        </div>
      </div>
    </section>
  );
}
